import AllasLogo from '@/allas/public/image/allas-logo.svg';
import { DynamicStandaloneButton } from '@/components/Button';
import { DynamicStandaloneLink } from '@/components/Link';
import { StandaloneFooter, StandaloneFooterProps } from 'base/components/Footer';
import { Footer } from 'base/components/Footer/Footer';
import GoogleNewsIcon from 'base/public/image/google-news.svg';
import { showOneTrustConsent } from 'lib/utils/oneTrust';

const currentYear = new Date().getFullYear();

const logo: StandaloneFooterProps['logo'] = <AllasLogo />;

const description: StandaloneFooterProps['description'] = (
  <>
    <a href="https://www.allas.se">Allas.se</a> erbjuder gripande journalistik kring ämnen som{' '}
    <a href="https://www.allas.se/relationer">relationer</a>, <a href="https://www.allas.se/halsa">hälsa</a> och{' '}
    <a href="https://www.allas.se/handarbete">handarbete</a> samt innehåll från veckotidningarna Allas, Allers, Hemmets
    Veckotidning, Året Runt, Allers <a href="https://www.allas.se/tradgard">Trädgård</a> och{' '}
    <a href="https://www.allas.se/etikett/antik och auktion">Antik & Auktion</a>.
  </>
);

const editors: StandaloneFooterProps['editors'] = 'Ansvarig utgivare: Åsa Liliegren';

const soMe: StandaloneFooterProps['soMe'] = (
  <Footer.SoMe.Group>
    <Footer.SoMe.Headline>Följ oss</Footer.SoMe.Headline>
    <Footer.SoMe
      href="https://www.facebook.com/allasveckotidning"
      name="facebook"
      aria-label="Länk till Facebook"
      options={{ colors: 'plain' }}
    />
    <Footer.SoMe
      href="https://www.instagram.com/allas.se"
      name="instagram"
      aria-label="Länk till Instagram"
      options={{ colors: 'plain' }}
    />
    <Footer.SoMe
      href="https://www.youtube.com/channel/UCbhwNRc_wF3tpAkM2feMTFA"
      name="youtube"
      aria-label="Länk till Youtube"
      options={{ colors: 'plain' }}
    />
    <Footer.SoMe
      href="https://www.pinterest.se/allas_se"
      name="pinterest"
      aria-label="Länk till Pinterest"
      options={{ colors: 'plain' }}
    />
    <DynamicStandaloneButton
      link={{
        href: 'https://news.google.com/publications/CAAqBwgKMPKfiwswqd3qAg?hl=sv&gl=SE&ceid=SE%3Asv',
        target: '_blank',
      }}
      options={{
        colors: 'secondary',
      }}
      content={
        <>
          <GoogleNewsIcon className="w-5" />
          <span className="whitespace-nowrap">Google nyheter</span>
        </>
      }
      aria-label="Länk till Google news"
    />
  </Footer.SoMe.Group>
);

const columns: StandaloneFooterProps['columns'] = (
  <>
    <Footer.Column>
      <Footer.List.Title>Om Allas</Footer.List.Title>
      <Footer.List>
        <Footer.List.Item>
          <DynamicStandaloneLink href="/nyhetsbrev" content="Nyhetsbrev" />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink href="/alla-amnen" content="Alla ämnen" />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink href="/vara-skribenter" content="Våra skribenter" />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink href="/om-cookies" content="Om cookies" />
        </Footer.List.Item>
        <Footer.List.Item>
          <button type="button" onClick={showOneTrustConsent}>
            Hantera preferenser
          </button>
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink
            href="https://www.aller.se/integritetspolicy"
            target="_blank"
            content="Integritetspolicy"
          />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink
            href="https://www.aller.se/ai-policy/"
            target="_blank"
            content="Aller Medias AI-policy"
          />
        </Footer.List.Item>
      </Footer.List>
    </Footer.Column>

    <Footer.Column>
      <Footer.List.Title>Allas.se</Footer.List.Title>
      <Footer.List>
        <Footer.List.Item>
          <DynamicStandaloneLink href="/livsoden" content="Livsöden" />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink href="/halsa" content="Hälsa" />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink href="/relationer" content="Relationer" />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink href="/handarbete" content="Handarbete" />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink href="/tradgard" content="Trädgård" />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink href="/noje" content="Nöje" />
        </Footer.List.Item>
      </Footer.List>
    </Footer.Column>

    <Footer.Column>
      <Footer.List.Title>Kontakta oss</Footer.List.Title>
      <Footer.List>
        <Footer.List.Item>
          <DynamicStandaloneLink href="/kontakta-oss" content="Kontakta oss" />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink href="https://ocast.com/se/aller-media" target="_blank" content="Annonsera hos oss" />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink href="https://prenumerera.se" target="_blank" content="Prenumerera" />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink href="https://prenumerera.se/tidningar/allas/" target="_blank" content="Allas" />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink href="https://prenumerera.se/tidningar/allers/" target="_blank" content="Allers" />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink
            href="https://prenumerera.se/tidningar/allers-tradgard/"
            target="_blank"
            content="Allers Trädgård"
          />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink
            href="https://prenumerera.se/tidningar/hemmets-veckotidning/"
            target="_blank"
            content="Hemmets Veckotidning"
          />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink
            href="https://prenumerera.se/tidningar/aret-runt/"
            target="_blank"
            content="Året runt"
          />
        </Footer.List.Item>
        <Footer.List.Item>
          <DynamicStandaloneLink
            href="https://prenumerera.se/tidningar/antik--auktion/"
            target="_blank"
            content="Antik auktion"
          />
        </Footer.List.Item>
      </Footer.List>
    </Footer.Column>
  </>
);

const copyright: StandaloneFooterProps['copyright'] = (
  <>
    <p>
      För prenumerationsärenden, ring{' '}
      <a className="hover:underline" href="tel:0424443013">
        042 444 30 00
      </a>{' '}
      • Ansvarig utgivare Åsa Liliegren © Copyright {currentYear}{' '}
      <a className="hover:underline" href="https://www.allas.se">
        allas.se
      </a>
    </p>
    <p>
      Allas är en del av{' '}
      <a className="hover:underline" target="_blank" href="https://www.aller.se">
        Aller media
      </a>
      . Humlegårdsgatan 6, 114 46 Stockholm.{' '}
      <a className="hover:underline" href="tel:086794600">
        08 679 46 00
      </a>
    </p>
  </>
);

export const AllasStandaloneFooter: typeof StandaloneFooter = () => {
  return <StandaloneFooter {...{ logo, description, editors, soMe, columns, copyright }} />;
};

export default AllasStandaloneFooter;
